/**
 * Build URL path segments (single segment) for embedded "cloudflare" applications.
 * Segments are derived from subTab; collisions and reserved first-path segments are disambiguated.
 */

export interface EmbedApp {
  id: string;
  tab: string;
  subTab: string;
  url: string;
}

/** First path segment reserved by the app (must not be used for embeds). */
const RESERVED_ROOT_SEGMENTS = new Set<string>([
  "events",
  "member",
  "settings",
  "nvr-live",
  "nvr-recording",
  "nvr-clip",
  "video-agent",
  "ai-status",
  "cmm",
  "nvr",
  "ai-analytics",
  "overview",
  "app",
  "embed",
  "static",
  "api",
  "public",
  "favicon",
  "login",
  "register",
  "forgot-password",
  "reset-password",
]);

export function slugifyPathSegment(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function buildCloudflarePathMaps(apps: EmbedApp[]): {
  byId: Map<string, string>;
  byPath: Map<string, EmbedApp>;
} {
  const sorted = [...apps].sort((a, b) => a.id.localeCompare(b.id));
  const byId = new Map<string, string>();
  const used = new Set<string>();

  for (const app of sorted) {
    const candidates: string[] = [
      slugifyPathSegment(app.subTab),
      slugifyPathSegment(`${app.tab}-${app.subTab}`),
    ].filter((s) => s.length > 0);

    candidates.push(`app-${app.id.split("-").join("").slice(0, 12)}`);

    let chosen = "";
    for (const c of candidates) {
      if (c && !used.has(c) && !RESERVED_ROOT_SEGMENTS.has(c)) {
        chosen = c;
        break;
      }
    }
    if (!chosen) {
      chosen = `app-${app.id.slice(0, 8)}`;
    }

    let finalSeg = chosen;
    let n = 0;
    while (used.has(finalSeg) || RESERVED_ROOT_SEGMENTS.has(finalSeg)) {
      n += 1;
      finalSeg = `${chosen}-${n}`;
    }
    used.add(finalSeg);
    byId.set(app.id, finalSeg);
  }

  const byPath = new Map<string, EmbedApp>();
  for (const app of apps) {
    const seg = byId.get(app.id);
    if (seg) {
      byPath.set(seg, app);
    }
  }
  return { byId, byPath };
}

export function isEmbedAppPathname(pathname: string, byPath: Map<string, EmbedApp>): boolean {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 2 && parts[0] === "embed") return byPath.has(parts[1]);
  return false;
}

/** Resolve a cloudflare app from the `/embed/:appEmbed` path segment. */
export function findEmbedAppByPathSegment(
  apps: EmbedApp[],
  pathSegment: string,
): EmbedApp | undefined {
  const segment = pathSegment.trim();
  if (!segment) return undefined;
  const { byPath } = buildCloudflarePathMaps(apps);
  return byPath.get(segment);
}

const EMBED_PATH_RE = /\/embed\/[^/?#]+/i;

/** Path segment after `/embed/` from an absolute or relative URL. */
export function extractEmbedPathSegment(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    const pathname = /^https?:\/\//i.test(trimmed)
      ? new URL(trimmed).pathname
      : trimmed.startsWith("/")
        ? trimmed.split(/[?#]/)[0]
        : `/${trimmed.split(/[?#]/)[0]}`;
    const parts = pathname.split("/").filter(Boolean);
    if (parts[0] === "embed" && parts[1]) return parts[1];
    return null;
  } catch {
    return null;
  }
}

/** Configured URL targets this same `/embed/:segment` route (infinite iframe loop). */
export function isSamePageEmbedLoop(configuredUrl: string, currentSegment: string): boolean {
  const seg = extractEmbedPathSegment(configuredUrl);
  const current = currentSegment.trim();
  if (!seg || !current) return false;
  return seg === current;
}

/** True when the configured URL would iframe this portal's own `/embed/…` route. */
export function isSelfReferentialEmbedUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      if (!EMBED_PATH_RE.test(parsed.pathname)) return false;
      if (typeof window === "undefined") return true;
      return parsed.origin === window.location.origin;
    } catch {
      return false;
    }
  }

  return EMBED_PATH_RE.test(trimmed) || /^embed\/[^/?#]+/i.test(trimmed);
}
